import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { FC, memo, useEffect, useMemo, useState } from 'react';

export type Props = {
  open: boolean;
  shipperNames: string[];
  isLoading: boolean;
  isError: boolean;
  onBack: () => void;
  onConfirm: (selected: string[]) => void;
};

const ShipperSelectDialog: FC<Props> = memo(({ open, shipperNames, isLoading, isError, onBack, onConfirm }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) setSelected(new Set());
  }, [open]);

  const allSelected = useMemo(
    () => shipperNames.length > 0 && shipperNames.every((name) => selected.has(name)),
    [shipperNames, selected],
  );

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(shipperNames));
  const clearAll = () => setSelected(new Set());

  const handleConfirm = () => {
    onConfirm(Array.from(selected));
  };

  return (
    <Dialog open={open} onClose={onBack} maxWidth="sm" fullWidth>
      <DialogTitle>荷主の選択</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          出力対象の荷主を選択してください（複数選択可）。
        </Typography>
        {isLoading && <Typography>読み込み中...</Typography>}
        {isError && <Typography color="error">荷主一覧の取得に失敗しました。</Typography>}
        {!isLoading && !isError && shipperNames.length === 0 && (
          <Typography color="text.secondary">期間内に荷主が登録されていません。</Typography>
        )}
        {!isLoading && !isError && shipperNames.length > 0 && (
          <>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 2, py: 1 }}>
              <List dense disablePadding>
                {shipperNames.map((name) => (
                  <ListItem key={name} disableGutters>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selected.has(name)}
                          onChange={() => toggle(name)}
                          size="small"
                        />
                      }
                      label={name}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Button size="small" onClick={selectAll} disabled={allSelected}>全選択</Button>
              <Button size="small" onClick={clearAll} disabled={selected.size === 0}>解除</Button>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onBack}>戻る</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={selected.size === 0}>
          出力へ進む
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default ShipperSelectDialog;
